<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const activeTab = ref('conversation');
const editingId = ref(null);

const editPrompt = reactive({
  label: '',
  content: '',
});

const newPrompt = reactive({
  label: '',
  content: '',
});

const isNewPromptValid = computed(() => {
  return newPrompt.label.trim() !== '' && newPrompt.content.trim() !== '';
});

const allPrompts = computed(() => store.getters['copilotPrompts/getRecords']);

const activePrompts = computed(() =>
  allPrompts.value.filter(
    p => p.context_type === activeTab.value && !p.deleted_at
  )
);

const deletedPrompts = computed(() =>
  allPrompts.value.filter(
    p => p.context_type === activeTab.value && p.deleted_at
  )
);

const uiFlags = computed(() => store.getters['copilotPrompts/getUIFlags']);

const fetchPrompts = async () => {
  if (!props.assistant?.id) return;
  try {
    await store.dispatch('copilotPrompts/fetch', {
      assistantId: props.assistant.id,
    });
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};

const addPrompt = async () => {
  if (!isNewPromptValid.value) return;
  try {
    await store.dispatch('copilotPrompts/create', {
      assistantId: props.assistant.id,
      promptData: {
        label: newPrompt.label.trim(),
        content: newPrompt.content.trim(),
        context_type: activeTab.value,
      },
    });
    newPrompt.label = '';
    newPrompt.content = '';
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};

const softDeletePrompt = async id => {
  try {
    await store.dispatch('copilotPrompts/delete', {
      assistantId: props.assistant.id,
      promptId: id,
    });
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};

const restorePrompt = async id => {
  try {
    await store.dispatch('copilotPrompts/restore', {
      assistantId: props.assistant.id,
      promptId: id,
    });
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};

const startEdit = prompt => {
  editingId.value = prompt.id;
  editPrompt.label = prompt.label;
  editPrompt.content = prompt.content;
};

const cancelEdit = () => {
  editingId.value = null;
  editPrompt.label = '';
  editPrompt.content = '';
};

const saveEdit = async id => {
  if (!editPrompt.label.trim() || !editPrompt.content.trim()) return;
  try {
    await store.dispatch('copilotPrompts/update', {
      assistantId: props.assistant.id,
      promptId: id,
      promptData: {
        label: editPrompt.label.trim(),
        content: editPrompt.content.trim(),
      },
    });
    cancelEdit();
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};

watch(
  () => props.assistant?.id,
  newId => {
    if (newId) fetchPrompts();
  },
  { immediate: true }
);

onMounted(() => {
  if (props.assistant?.id) fetchPrompts();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Tabs -->
    <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-2">
      <button
        class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
        :class="activeTab === 'conversation'
          ? 'bg-n-solid text-n-on-solid shadow-sm'
          : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-3'"
        @click="activeTab = 'conversation'; cancelEdit()"
      >
        {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.TAB_CONVERSATION') }}
      </button>
      <button
        class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
        :class="activeTab === 'dashboard'
          ? 'bg-n-solid text-n-on-solid shadow-sm'
          : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-3'"
        @click="activeTab = 'dashboard'; cancelEdit()"
      >
        {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.TAB_DASHBOARD') }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="uiFlags.isFetching" class="text-sm text-n-slate-11 py-2">
      ...
    </div>

    <!-- Active prompts list -->
    <template v-else>
      <div v-if="activePrompts.length > 0" class="flex flex-col gap-3">
        <div
          v-for="prompt in activePrompts"
          :key="prompt.id"
          class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-slate-2"
        >
          <!-- View mode -->
          <template v-if="editingId !== prompt.id">
            <div class="flex items-start gap-3">
              <div class="flex-1 flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ prompt.label }}
                  </span>
                  <span
                    v-if="prompt.is_default"
                    class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-blue-3 text-n-blue-11"
                  >
                    {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.DEFAULT_BADGE') }}
                  </span>
                </div>
                <span class="text-xs text-n-slate-11 line-clamp-2">
                  {{ prompt.content }}
                </span>
              </div>
              <div class="flex-shrink-0 flex gap-1">
                <button
                  class="p-1 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-n-blue-11 transition-colors"
                  @click="startEdit(prompt)"
                >
                  <i class="i-lucide-pencil size-4" />
                </button>
                <button
                  class="p-1 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-ruby-11 transition-colors"
                  @click="softDeletePrompt(prompt.id)"
                >
                  <i class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>
          </template>

          <!-- Edit mode -->
          <template v-else>
            <Input
              v-model="editPrompt.label"
              :label="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.LABEL_FIELD')"
            />
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.CONTENT_FIELD') }}
              </label>
              <textarea
                v-model="editPrompt.content"
                class="w-full reset-base bg-n-alpha-3 px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-0 focus:ring-2 focus:ring-n-blue-11 focus:border-n-blue-11 resize-y text-n-slate-12 mb-0"
                rows="3"
              />
            </div>
            <div class="flex gap-2">
              <Button
                :label="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.SAVE_EDIT_BUTTON')"
                xs
                :is-loading="uiFlags.isUpdating"
                @click="saveEdit(prompt.id)"
              />
              <Button
                :label="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.CANCEL_BUTTON')"
                xs
                faded
                @click="cancelEdit"
              />
            </div>
          </template>
        </div>
      </div>

      <div v-else class="text-sm text-n-slate-11 py-2">
        {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.EMPTY_STATE') }}
      </div>

      <!-- Deleted prompts (soft delete - restorable) -->
      <div v-if="deletedPrompts.length > 0" class="flex flex-col gap-3">
        <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wider">
          {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.DELETED_SECTION') }}
        </span>
        <div
          v-for="prompt in deletedPrompts"
          :key="prompt.id"
          class="flex items-center gap-3 p-3 rounded-lg border border-dashed border-n-weak bg-n-alpha-1 opacity-60"
        >
          <div class="flex-1 flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-11 line-through">
              {{ prompt.label }}
            </span>
          </div>
          <Button
            :label="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.RESTORE_BUTTON')"
            xs
            faded
            icon="i-lucide-undo-2"
            @click="restorePrompt(prompt.id)"
          />
        </div>
      </div>

      <!-- Add new prompt form -->
      <div class="flex flex-col gap-3 p-4 rounded-lg border border-dashed border-n-weak bg-n-alpha-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wider">
          {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.ADD_NEW') }}
        </span>
        <Input
          v-model="newPrompt.label"
          :label="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.LABEL_FIELD')"
          :placeholder="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.LABEL_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.CONTENT_FIELD') }}
          </label>
          <textarea
            v-model="newPrompt.content"
            :placeholder="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.CONTENT_PLACEHOLDER')"
            class="w-full reset-base bg-n-alpha-3 px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-0 focus:ring-2 focus:ring-n-blue-11 focus:border-n-blue-11 resize-y text-n-slate-12 mb-0"
            rows="3"
          />
        </div>
        <div>
          <Button
            :label="t('CAPTAIN.COPILOT.PROMPTS_SETTINGS.ADD_BUTTON')"
            icon="i-lucide-plus"
            xs
            :disabled="!isNewPromptValid"
            :is-loading="uiFlags.isCreating"
            @click="addPrompt"
          />
        </div>
      </div>
    </template>
  </div>
</template>
